import { LoadingButton } from '@mui/lab'
import React from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import logoJira from 'assets/logos/jira.svg'
import Image from 'components/common/Image'
import JiraOAuthButton from 'components/rbac-settings/integrations/jira/JiraOAuthButton'
import Row from 'components/Row'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { messageFromResponseError } from 'utils/strings'

export const JiraIntegration: React.FC<{
  isConnected: boolean
  onChanged?: () => void
}> = ({ isConnected, onChanged }) => {
  const { currentTeamId } = useAuth()
  const [
    isJiraIntegrationRemovalInProgress,
    setIsJiraIntegrationRemovalInProgress
  ] = useBoolean(false)

  const removeJiraIntegration = async () => {
    setIsJiraIntegrationRemovalInProgress(true)

    try {
      await agent.delete(
        `/external-integrations?type=jira&teamId=${currentTeamId}`
      )
      onChanged()
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was an issue removing this Jira integration. Please try again later.'
        )
      })
    } finally {
      setIsJiraIntegrationRemovalInProgress(false)
    }
  }

  return (
    <Row
      action={
        isConnected ? (
          <LoadingButton
            data-testid="jira-integration-revoke-button"
            disabled={isJiraIntegrationRemovalInProgress}
            loading={isJiraIntegrationRemovalInProgress}
            variant="contained"
            onClick={removeJiraIntegration}
          >
            Remove
          </LoadingButton>
        ) : (
          <JiraOAuthButton onChange={onChanged} />
        )
      }
      data-testid="jira-integration-row"
      label={
        <Image alt="Jira" css={{ height: theme.space[5] }} src={logoJira} />
      }
      value={
        isConnected
          ? 'Connected'
          : 'Sign in to Jira using your personal credentials'
      }
    />
  )
}
